<template>
  <CommonPages id="detail" class="detail" headerTheme="dark" :titlePrefix="formattedCarName"
    :metaDescription="description">
    <div class="detail__container">

      <!-- 1. 顶部画廊区域 (图二/图三) -->
      <div class="detail__gallery">
        <div class="detail__gallery-main-wrapper">
          <div class="detail__gallery-main">
            <img v-if="currentImage" :src="currentImage" alt="car image" class="detail__gallery-img">
            <div class="detail__gallery-shadow"></div>
         
          </div>
        </div>

        <AdComponent v-if="deviceType !== 'mobile' && $global.isValidAdConfig(adsensConfig?.detail_1)"
          ref="ads-detail-1-pc" class="detail-top-ad-padding" :ads="adsensConfig?.detail_1"
          :showDebug="showDebug" />

        <!-- 标题和品牌区域 + 画廊切换按钮 -->
        <div class="detail__header-section">
          <div class="detail__header-content">
            <div class="detail__brand-logo-wrapper">
              <img :src="carData?.logoUrl || carData?.logo || ''" class="detail__brand-logo"
                alt="brand logo">
            </div>
            <div class="detail__header-info">
              <h1 class="detail__title">{{ carData?.model || 'Loading...' }}</h1>
              <div class="detail__brand-name">{{ carData?.brand || '' }}</div>
            </div>
            <!-- PC端画廊切换按钮移到这里 (图二) -->
            <div class="detail__gallery-controls" v-if="galleryLength > 1">
              <button class="detail__gallery-nav" @click="prevImage">
                <svg xmlns="http://www.w3.org/2000/svg" width="6" height="12" viewBox="0 0 6 12"
                  fill="none">
                  <path
                    d="M1.81151 6L5.78094 1.46355C6.07302 1.12975 6.07302 0.584148 5.78094 0.250349C5.48887 -0.0834497 5.01146 -0.0834497 4.71938 0.250349L0.218581 5.39407C0.145855 5.47718 0.0907245 5.5737 0.0543615 5.67693C-0.00898042 5.8579 -0.0171914 6.05898 0.0309015 6.24532C0.0379395 6.27213 0.0461505 6.2976 0.0543615 6.32441C0.0907245 6.42764 0.145855 6.52416 0.218581 6.60727L4.71821 11.7497C5.01028 12.0835 5.48769 12.0835 5.77977 11.7497C6.07185 11.4159 6.07185 10.8702 5.77977 10.5364L1.81151 6Z"
                    fill="white" />
                </svg>
              </button>
              <button class="detail__gallery-nav" @click="nextImage">
                <svg xmlns="http://www.w3.org/2000/svg" width="6" height="12" viewBox="0 0 6 12"
                  fill="none">
                  <path
                    d="M4.18849 6L0.219058 1.46355C-0.073019 1.12975 -0.073019 0.584148 0.219058 0.250349C0.511134 -0.0834497 0.988545 -0.0834497 1.28062 0.250349L5.78142 5.39407C5.85414 5.47718 5.90928 5.5737 5.94564 5.67693C6.00898 5.8579 6.01719 6.05898 5.9691 6.24532C5.96206 6.27213 5.95385 6.2976 5.94564 6.32441C5.90928 6.42764 5.85414 6.52416 5.78142 6.60727L1.28179 11.7497C0.989717 12.0835 0.512307 12.0835 0.22023 11.7497C-0.0718465 11.4159 -0.0718465 10.8702 0.22023 10.5364L4.18849 6Z"
                    fill="white" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- 缩略图区域 (图一/图二) -->
        <div class="detail__thumbnails-container">
          <div class="detail__thumbnails" ref="thumbnailsContainer">
            <div v-for="(image, index) in carData?.gallery" :key="`thumb-${index}`"
              class="detail__thumbnail-item"
              :class="{'detail__thumbnail-item--active': currentImageIndex === index}"
              ref="thumbnailItems"
              @click="setCurrentImage(index)">
              <img :src="image" alt="car thumbnail" class="detail__thumbnail-img">
            </div>
          </div>
        </div>
      </div>

      <!-- 虚线分隔线 (图三) -->
      <div class="detail__divider-line"></div>

      <!-- 3. 主体内容区域 (Specs + Info) -->
      <div class="detail__main-content">
        <!-- 左侧 规格参数 (图三) -->
        <div class="detail__specs-sidebar">
          <div v-for="(card, index) in infoCards" :key="`spec-${index}`" class="detail__spec-card">
            <div class="detail__spec-icon">
              <img :src="card.icon" :alt="card.label">
            </div>
            <div class="detail__spec-info">
              <div class="detail__spec-value">{{ card.value }}</div>
              <div class="detail__spec-label">{{ card.label }}</div>
            </div>
          </div>
        </div>

        <!-- 纵向虚线 (图三) -->
        <div class="detail__vertical-divider"></div>

        <!-- 右侧 详细文本 (图三) -->
        <div class="detail__info-sections">
          <div v-for="(section, index) in sections" :key="`section-row-${index}`" class="detail__section-item">
            <!-- Mobile 新位(截图黄格): detail_1 Overall上方 / detail_2 Safety上方 / detail_3 Fuel Economy上方 -->
            <AdComponent v-if="deviceType === 'mobile' && section.title === 'Overall' && $global.isValidAdConfig(adsensConfig?.detail_1)"
              class="detail__mobile-inline-ad"
              :ref="'ads-detail-1-' + index" :ads="adsensConfig?.detail_1"
              :showDebug="showDebug" />

            <AdComponent v-if="deviceType === 'mobile' && section.title === 'Safety' && $global.isValidAdConfig(adsensConfig?.detail_2)"
              class="detail__mobile-inline-ad"
              :ref="'ads-detail-2-' + index" :ads="adsensConfig?.detail_2"
              :showDebug="showDebug" />

            <AdComponent v-if="deviceType === 'mobile' && section.title === 'Fuel Economy' && $global.isValidAdConfig(adsensConfig?.detail_3)"
              class="detail__mobile-inline-ad"
              :ref="'ads-detail-3-' + index" :ads="adsensConfig?.detail_3"
              :showDebug="showDebug" />

            <div class="detail__section">
              <h2 class="detail__section-title">{{ section.title }}</h2>
              <div class="detail__section-body" v-html="section.content"></div>
            </div>

            <!-- Mobile: 在 Safety 后面插入横滑 Similar (图四) -->
            <div v-if="deviceType === 'mobile' && section.title === 'Safety'"
              class="detail__similar-mobile-scroll">
              <div class="detail__similar-header">
                <h2 class="detail__similar-title">Similar Priced Cars</h2>
              </div>
              <div class="detail__similar-list--scroll">
                <a v-for="(car, carIndex) in similarCarsTop" :key="`sim-top-${carIndex}`"
                  class="detail__similar-card scrolled" @click="gotoDetail(car)">

                  <div class="detail__similar-card-img">
                    <img :src="car.image">
                    <div class="detail__similar-card-logo"><img :src="car.logo"></div>
                    <div class="detail__similar-card-rating" v-if="car.rate">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"
                        viewBox="0 0 24 24" fill="#fff">
                        <path
                          d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                      <span>{{ car.rate }}</span>
                    </div>
                  </div>
                  <div class="detail__similar-card-info">
                    <h3 class="detail__similar-card-title">{{ car.model }}</h3>
                    <p class="detail__similar-card-brand">{{ car.brand }}</p>
                  </div>
                  <div class="detail__similar-card-tags">
                    <div class="detail__similar-tag"><img
                        src="@/assets/calendar.svg"><span>{{ car.year }}</span></div>
                    <div class="detail__similar-tag"><img
                        src="@/assets/car-type.svg"><span>{{ car.fuel }}</span></div>
                    <div class="detail__similar-tag"><img
                        src="@/assets/body-type.svg"><span>{{ car.category }}</span></div>
                    <div class="detail__similar-tag"><img
                        src="@/assets/homehero/Budget.svg"><span>{{ car.price }}</span></div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 4. 相似价格车辆 - 底部纵向展开区 (PC全部/Mobile底部) -->
      <div class="detail__similar-section">
        <div class="detail__similar-header">
          <h2 class="detail__similar-title">Similar Priced Cars</h2>
        </div>

        <div class="detail__similar-grid">
          <a v-for="(car, index) in displayedSimilarCars" :key="`sim-grid-${index}`"
            class="detail__similar-card" @click="gotoDetail(car)">
            <div class="detail__similar-card-img">
              <img :src="car.image">
              <div class="detail__similar-card-logo"><img :src="car.logo"></div>
              <div class="detail__similar-card-rating" v-if="car.rate">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
                  fill="#fff">
                  <path
                    d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                </svg>
                <span>{{ car.rate }}</span>
              </div>
            </div>

            <div class="detail__similar-card-info">
              <h3 class="detail__similar-card-title">{{ car.model }}</h3>
              <p class="detail__similar-card-brand">{{ car.brand }}</p>
            </div>
            <div class="detail__similar-card-tags">
              <div class="detail__similar-tag"><img
                  src="@/assets/calendar.svg"><span>{{ car.year }}</span></div>
              <div class="detail__similar-tag"><img
                  src="@/assets/car-type.svg"><span>{{ car.fuel }}</span></div>
              <div class="detail__similar-tag"><img
                  src="@/assets/body-type.svg"><span>{{ car.category }}</span></div>
              <div class="detail__similar-tag"><img
                  src="@/assets/homehero/Budget.svg"><span>{{ car.price }}</span></div>
            </div>
          </a>
        </div>
      </div>

    </div>
  </CommonPages>
</template>

<script>
import common from '@/mixin/common.js'
import { mapGetters } from 'vuex';
import CommonPages from '@/pages/components/page_common.vue'
import reportMixin from '@/mixin/reportMixin.js'
import adMixin from '@/mixin/adMixin.js'
import adTrackingMixin from '@/mixin/adTrackingMixin.js'
import AdComponent from '@/pages/components/AdComponent.vue'

export default {
  name: 'Detail',
  components: {
    CommonPages,
    AdComponent
  },
  mixins: [common, reportMixin, adMixin, adTrackingMixin],
  props: {
    carName: { type: String, default: '' },
    id: { type: String, default: '' }
  },
  data () {
    return {
      currentImageIndex: 0,
      carData: { gallery: [] },
      similarCars: [],
      sections: [],
      loading: false
    }
  },
  computed: {
    ...mapGetters(['deviceType', 'domainConfig', 'adsensConfig', 'showDebug']),
    formattedCarName () { return this.carName ? decodeURIComponent(this.carName).replace(/-/g, ' ') : ''; },
    description () { return `${this.domainConfig?.['meta']?.des || 'Default des'}` },
    currentImage () {
      if (!this.carData?.gallery?.length) return '';
      return this.carData.gallery[this.currentImageIndex] || '';
    },
    galleryLength () { return this.carData?.gallery?.length || 0; },
    similarCarsTop () { return this.similarCars.slice(0, 4); },
    similarCarsBottom () { return this.similarCars.slice(4, 8); },
    displayedSimilarCars () {
      return this.deviceType === 'mobile' ? this.similarCarsBottom : this.similarCars;
    },
    infoCards () {
      return [
        { icon: require('../assets/detail-icons/traffic_jam.svg'), value: this.carData?.type || '-', label: 'Type' },
        { icon: require('../assets/detail-icons/calendar_month.svg'), value: this.carData?.year || '-', label: 'Ex-Showroom Time' },
        { icon: require('../assets/detail-icons/paid.svg'), value: this.carData?.price || '-', label: 'Ex-Showroom Price' },
        { icon: require('../assets/detail-icons/local_gas_station.svg'), value: this.carData?.fuel || '-', label: 'Fuel' },
        { icon: require('../assets/detail-icons/crossword.svg'), value: this.carData?.purpose || '-', label: 'Purpose' },
        { icon: require('../assets/detail-icons/award_star.svg'), value: this.carData?.rate || '-', label: 'Rate' },
        { icon: require('../assets/detail-icons/readiness_score.svg'), value: this.carData?.speedValue || '-', label: '0-60-MPH Times' }
      ];
    }
  },
  watch: {
    id: {
      immediate: true,
      handler () { this.fetchCarInfo(); }
    },
    currentImageIndex (newIndex) {
      this.scrollToThumbnail(newIndex);
    }
  },
  methods: {
    async fetchCarInfo () {
      if (!this.id) return;
      try {
        this.loading = true;
        const webIndex = this.domainConfig?.webIndex || 1;
        const themeFilters = this.domainConfig?.themeFilters || {};
        const res = await this.$global.server.getCarInfo({ id: this.id, ...themeFilters, webIndex });
        if (res.code === 200 && res.data) {
          const carInfo = res.data.carInfo || res.data;
          const photos = carInfo.photos ? carInfo.photos.split(',') : [];
          this.carData = {
            ...carInfo,
            gallery: photos.length ? photos : '',
            model: carInfo.name,
            marketTime: carInfo.marketTime,
            year: this.formatDate(carInfo.marketTime) || carInfo.year || '-',
            price: this.formatPrice(carInfo.leastPrice, carInfo.mostPrice),
            speedValue: carInfo.accelerationTime ? `${carInfo.accelerationTime}s` : '-'
          };
          this.sections = [
            { title: 'Overall', content: carInfo.overall },
            { title: 'Engine, Transmission, and Performance', content: carInfo.enginePerformance || carInfo.endurance },
            { title: 'Safety', content: carInfo.safety },
            { title: 'Fuel Economy', content: carInfo.fuelEconomy },
            { title: 'Interior and Comfort', content: carInfo.interiorAndComfort },
            { title: 'Infotainment and Connectivity', content: carInfo.infotainmentAndConnectivity },
            { title: 'Warranty and Maintenance Coverage', content: carInfo.warrantyAndMaintenanceCoverage }
          ].filter(s => s.content && s.content.trim());

          if (res.data.recommendList) {
            this.similarCars = res.data.recommendList.map(item => this.formatCarData(item));
          } else {
            this.fetchSimilarCars();
          }
        }
      } catch (e) { console.error(e); } finally { this.loading = false; }
    },
    async fetchSimilarCars () {
      try {
        const themeFilters = this.domainConfig?.themeFilters || {};
        const res = await this.$global.server.searchCar({ pageSize: 8, brand: this.carData.brand, ...themeFilters });
        if (res.code === 200 && res.data?.list) {
          this.similarCars = res.data.list.map(item => this.formatCarData(item));
        }
      } catch (e) { console.error(e); }
    },
    formatCarData (item) {
      return {
        ...item,
        logo: item.logoUrl || item.logo,
        image: item.cover || (item.photos ? item.photos.split(',')[0] : ''),
        model: item.name,
        year: this.formatDate(item.marketTime) || item.year || '-',
        marketTime: item.marketTime,
        price: this.formatPrice(item.leastPrice, item.mostPrice),
        bodyType: item.type || '-',
        category: item.purpose || '-',
        rate: item.rate || '9.0'
      };
    },
    formatDate (dateStr) {
      if (!dateStr || typeof dateStr !== 'string') return '-';
      const parts = dateStr.split('-');
      if (parts.length < 2) return dateStr;
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const year = parts[0];
      const monthIdx = parseInt(parts[1], 10) - 1;
      if (monthIdx >= 0 && monthIdx < 12) {
        return `${months[monthIdx]} ${year}`;
      }
      return dateStr;
    },
    formatPrice (least, most) {
      if (!least && !most) return '-';
      const fmt = (n) => n ? '$' + n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '';
      if (least && most) return `${fmt(least)}-${fmt(most)}`;
      return least ? `>${fmt(least)}` : `<${fmt(most)}`;
    },
    setCurrentImage (index) { this.currentImageIndex = index; },
    scrollToThumbnail (index) {
      this.$nextTick(() => {
        const container = this.$refs.thumbnailsContainer;
        const items = this.$refs.thumbnailItems;
        if (container && items && items[index]) {
          const item = items[index];
          const containerWidth = container.offsetWidth;
          const itemWidth = item.offsetWidth;
          const itemLeft = item.offsetLeft;

          // 计算滚动位置，尽可能让激活的缩略图在容器中间显示
          const targetScrollLeft = itemLeft - (containerWidth / 2) + (itemWidth / 2);

          container.scrollTo({
            left: targetScrollLeft,
            behavior: 'smooth'
          });
        }
      });
    },
    prevImage () { this.currentImageIndex = (this.currentImageIndex - 1 + this.galleryLength) % this.galleryLength; },
    nextImage () { this.currentImageIndex = (this.currentImageIndex + 1) % this.galleryLength; },
    gotoDetail (car) {
      const channel = this.$route.params.channel;
      const name = encodeURIComponent(car.model.replace(/\s+/g, '-'));
      const path = channel ? `/${channel}/cars/${name}/${car.id}` : `/cars/${name}/${car.id}`;
      this.$router.push(path);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/css/variables.scss';

.detail {
	background: #000;
	min-height: 100vh;
	color: #fff;

	&__mobile-inline-ad {
		width: 100%;
	}

	&__section-item {
		display: contents;
	}

	&__container {
		max-width: rem(1440);
		margin: 0 auto;
		padding-bottom: rem(100);
	}

	/* Gallery Section */
	&__gallery {
		width: 100%;
		background: #000;
	}

	&__gallery-main-wrapper {
		position: relative;
		width: 100%;
		height: rem(600);
		display: flex;
		justify-content: center;
		align-items: center;
		overflow: hidden;
	}

	&__gallery-main {
		position: relative;
		width: 100%;
		height: 100%;
		max-width: rem(1200);
	}

  &__gallery-shadow{
    width: 100%;
    height: 100%;
    background: url('../assets/mask-detail-gallery.png') no-repeat center center;
    background-size: 100% 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
  }

	&__gallery-img {
		height: 100%;
		object-fit: cover;
	}

	/* Header Section (PC端切换按钮在右侧) */
	&__header-section {
		padding: rem(40) rem(180) rem(0);
	}

	&__header-content {
		display: flex;
		align-items: center;
		gap: rem(20);
		position: relative;
	}

	&__brand-logo-wrapper {
		width: rem(58);
		height: rem(58);
		background: #fff;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: rem(6);
	}

	&__brand-logo {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	&__header-info {
		flex: 1;
	}

	&__title {
		font-size: rem(32);
		font-weight: 800;
		margin: 0;
	}
	&__brand-name {
		font-size: rem(14);
		color: rgba(255, 255, 255, 0.6);
		margin-top: rem(4);
	}

	&__gallery-controls {
		display: flex;
		gap: rem(12);
	}

	&__gallery-nav {
		width: rem(24);
		height: rem(24);
		background: #e2372d;
		border-radius: rem(8);
		border: none;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 0.3s;
		&:hover {
			opacity: 0.8;
		}
	}

	&__thumbnails-container {
		padding: rem(24) rem(180);
		display: flex;
		justify-content: flex-start;
	}

	&__thumbnails {
		display: flex;
		gap: rem(12);
		overflow-x: auto;
		scrollbar-width: none;
		&::-webkit-scrollbar {
			display: none;
		}
	}

	&__thumbnail-item {
		width: rem(120);
		height: rem(80);
		border-radius: rem(12);
		border: 2px solid transparent;
		overflow: hidden;
		cursor: pointer;
		flex-shrink: 0;
		transition: all 0.3s;
		&--active {
			border-color: #e2372d;
		}
	}

	&__thumbnail-img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	/* 虚线分隔线 (图三) */
	&__divider-line {
		margin: 0 rem(180) rem(40);
		height: 1px;
		border-top: 1px dashed rgba(255, 255, 255, 0.2);
	}

	/* Main Content Area */
	&__main-content {
		display: flex;
		padding: rem(0) rem(180) rem(60);
		gap: rem(40);
	}

	&__specs-sidebar {
		width: rem(280);
		display: flex;
		flex-direction: column;
		gap: rem(16);
		flex-shrink: 0;
	}

	&__spec-card {
		background: linear-gradient(180deg, rgba(226, 55, 45, 0) 0%, rgba(226, 55, 45, 0.2) 100%);
		border: 1px solid rgba(255, 255, 255, 0.1);
		border-radius: rem(16);
		padding: rem(16) rem(20);
		display: flex;
		align-items: center;
		gap: rem(12);
		transition: transform 0.3s;
		&:hover {
			transform: translateY(-4px);
		}
	}

	&__spec-icon {
		width: rem(32);
		height: rem(32);
		display: flex;
		align-items: center;
		justify-content: center;
		background: #e2372d;
		border-radius: rem(8);
		img {
			width: rem(16);
			height: rem(16);
		}
	}

	&__spec-value {
		font-size: rem(14);
		font-weight: 700;
	}
	&__spec-label {
		font-size: rem(12);
		color: rgba(255, 255, 255, 0.5);
	}

	&__vertical-divider {
		width: 1px;
		border-left: 1px dashed rgba(255, 255, 255, 0.2);
		align-self: stretch;
	}

	&__info-sections {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: rem(40);
	}
	&__section-title {
		font-size: rem(24);
		font-weight: 800;
		border-bottom: 1px dashed rgba(255, 255, 255, 0.2);
		padding-bottom: rem(12);
		margin-bottom: rem(20);
	}
	&__section-body {
		font-size: rem(14);
		line-height: 1.8;
		color: rgba(255, 255, 255, 0.7);
	}

	/* Similar Section */
	&__similar-section {
		padding: rem(60) rem(180);
		border-top: 1px dashed rgba(255, 255, 255, 0.2);
	}

	&__similar-title {
		font-size: rem(32);
		font-weight: 800;
		margin-bottom: rem(40);
	}

	&__similar-grid {
		display: flex;
		flex-wrap: wrap;
		gap: rem(30);
		.detail__similar-card {
			width: calc(25% - rem(22.5)); // 一行4个
		}
	}

	/* Similar Card Style */
	&__similar-card {
		background: linear-gradient(180deg, rgba(226, 55, 45, 0) 0%, rgba(226, 55, 45, 0.2) 100%);
		border: 1px solid rgba(255, 255, 255, 0.1);
		border-radius: rem(16);
		padding: rem(8);
		display: flex;
		flex-direction: column;
		cursor: pointer;
		transition: transform 0.3s;
		&:hover {
			transform: translateY(-5px);
			border-color: rgba(226, 55, 45, 0.5);
		}
		&.scrolled {
			width: rem(252);
		}
	}

	&__similar-card-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: rem(12);
	}

	&__similar-card-logo {
		position: absolute;
		top: 0;
		left: 0;
		width: rem(32);
		height: rem(32);
		background: #fff;
		border-radius: rem(12) 0 rem(12) 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: rem(4);
		img {
			width: 80%;
			height: 80%;
			object-fit: contain;
		}
	}

	&__similar-card-rating {
		position: absolute;
		top: 0;
		min-width: rem(34);
		left: rem(32);
		background: #e2372d;
		padding: rem(4) rem(8);
		border-radius: 0 0 rem(12) 0;
		display: flex;
		align-items: center;
		gap: rem(4);
		span {
			color: #fff;
			font-size: rem(12);
			font-weight: 700;
		}
	}

	&__similar-card-img {
		position: relative;
		height: rem(200);
		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			border-radius: rem(12);
		}
	}

	&__similar-card-info {
		display: flex;
		justify-content: space-between;
		gap: rem(20);
		padding: rem(16) rem(8);
	}

	&__similar-card-title {
		font-weight: 800;
		margin: 0;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		overflow: hidden;
		color: #fff;
		text-overflow: ellipsis;
		font-family: Inter;
		font-size: rem(16);
		font-style: normal;
		font-weight: 800;
		line-height: rem(16); /* 100% */
		margin: 0;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		flex: 1; /* 占据剩余空间，允许收缩 */
		min-width: 0; /* 防止 flex-shrink 失效 */
	}
	&__similar-card-brand {
		font-size: rem(12);
		color: rgba(255, 255, 255, 0.5);
		overflow: hidden;
		color: #fff;
		text-overflow: ellipsis;
		font-family: Inter;
		font-style: normal;
		font-weight: 200;
		line-height: rem(12); /* 100% */
		margin: 0;
		flex-shrink: 0; /* 禁止收缩，确保品牌始终完整显示 */
		white-space: nowrap; /* 防止品牌换行 */
	}

	&__similar-card-tags {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: rem(8);
		padding: 0 rem(8);
	}

	&__similar-tag {
		border-radius: rem(12);
		border: 1px solid rgba(255, 255, 255, 0.1);
		padding: rem(10) rem(12);
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: rem(8);
		border: 1px solid rgba(255, 255, 255, 0.1);
		background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.1) 100%);
		min-width: 0; // 确保省略号生效的关键

		img {
			width: rem(24);
			height: rem(24);
			// filter: brightness(0) invert(1);
			flex-shrink: 0;
		}

		span {
			color: #ffffff;
			font-size: rem(12);
			font-weight: 600;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			width: 100%;
			text-align: center;
		}
	}
}

.detail-top-ad-padding {
	padding: 0 rem(180);
	box-sizing: border-box;
}

/* Mobile Adjustments */
.detect_deviece_mobile {
	.detail-top-ad-padding {
		padding: 0 rem(16);
	}
	.detail {
		&__gallery-main-wrapper {
			height: rem(250);
		}
		&__thumbnails-container {
			padding: rem(10) 0 rem(10) rem(16);
		}
		&__thumbnail-item {
			width: rem(80);
			height: rem(54);
		}

		&__header-section {
			padding: rem(20) rem(16);
		}
		&__title {
			font-size: rem(24);
		}
		&__gallery-controls {
			display: none;
		}

		&__divider-line {
			margin: 0 rem(16) rem(20);
		}

		&__main-content {
			flex-direction: column;
			padding: 0 rem(16) rem(40);
		}
		&__specs-sidebar {
			width: calc(100% + rem(16));
			flex-direction: row;
			overflow-x: auto;
			padding-bottom: rem(10);
			scrollbar-width: none;
			margin-right: rem(-16);
			&::-webkit-scrollbar {
				display: none;
			}
		}
		&__spec-card {
			flex-shrink: 0;
			min-width: rem(160);
		}

		&__vertical-divider {
			display: none;
		}

		&__section-title {
			font-size: rem(20);
		}

		&__similar-section {
			padding: rem(40) rem(16);
		}
		&__similar-title {
			font-size: rem(24);
		}

		&__similar-mobile-scroll {
			margin: rem(20) 0 rem(40);
			.detail__similar-title {
				font-size: rem(20);
				margin-bottom: rem(20);
			}
		}
		&__similar-list--scroll {
			display: flex;
			gap: rem(16);
			overflow-x: auto;
			scrollbar-width: none;
			margin-right: rem(-16);
			&::-webkit-scrollbar {
				display: none;
			}
			.detail__similar-card {
				min-width: rem(280);
				flex-shrink: 0;
			}
		}

		&__similar-grid {
			flex-direction: column;
			.detail__similar-card {
				width: 100%;
			}
		}
	}
}
</style>
